import { Accessor, ComponentProps, createEffect, onCleanup, onMount, ParentProps, Show, splitProps, ValidComponent } from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { DropdownContext, requireDropdownContext } from "common";
import style from '../st-dropdown.module.css'
import { dom } from 'common'

export type DropdownContentProps<T extends ValidComponent> = {
    as?: T
} & ComponentProps<T>

// Track the stack of dropdowns so we can close only the topmost dropdown
const ddStack: DropdownContext[] = [];
function push(ctx: DropdownContext) {
    if (ddStack.indexOf(ctx) > -1) return;
    ddStack.unshift(ctx);
}

function pop(ctx: DropdownContext) {
    if (ddStack.indexOf(ctx) === -1) return;

    requestAnimationFrame(() => {
        if (ddStack[0] && ddStack[0] !== ctx) {
            const c = ddStack.shift();
            c?.setIsOpen(false);
        }
        ddStack.shift();
    });
}

document.addEventListener('mousedown', globalClickHandler)
document.addEventListener('keydown', globalKeyHandler)

function globalClickHandler(ev: MouseEvent) {
    // Loop through the stack starting from the top-most dropdown.
    // If the click was inside the dropdown, we stop looping.
    // If the click was oustide the dropdown, we close it and move to the next layer
    for (const dd of ddStack) {
        if (dd.content?.contains(ev.target as Node))
            break;

        if (dd.trigger?.contains(ev.target as Node))
            break;

        dd.toggle(false);
    }
}

function globalKeyHandler(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
        ddStack[0]?.toggle(false);
    }
}

export function DropdownContent<T extends ValidComponent = 'div'>(props: ParentProps<DropdownContentProps<T>>) {
    const ddContext = requireDropdownContext()

    return (
        <Show when={ddContext.isOpen()}>
            <Portal>
                <ContentInternal {...props} />
            </Portal>
        </Show>
    )
}

function ContentInternal<T extends ValidComponent = 'div'>(props: ParentProps<DropdownContentProps<T>>) {
    const [local, other] = splitProps(props, ['as'])
    const ddContext = requireDropdownContext()
    
    createEffect(() => {
        if (ddContext.isOpen()) {
            push(ddContext);
        } else {
            pop(ddContext);
        }
    })

    const initRef = (el: HTMLElement) => {
        ddContext.registerContent(el);
    }

    onMount(() => {
        createEffect(() => {
            if (!ddContext.isOpen()) return;
            
            let zIndex = 1024;
            let el = ddContext.content?.parentElement as Element | null | undefined;

            while (el = el?.previousElementSibling) {
                if (el instanceof HTMLElement && dom.isAnyDescendantVisible(el)) {
                    const z = parseInt(getComputedStyle(el).zIndex);
                    if (z < 10_000 && z > zIndex) zIndex = z;
                }
            }

            ddContext.content?.parentElement?.style.setProperty('z-index', `${zIndex + 1}`);
        })
    })

    const defaultRole = () => {
        const p = ddContext.popup();
        return p === 'true' ? 'dialog' : p;
    };

    return (
        <div
            ref={initRef}
            class={style['dropdown-content-overlay']}
            style={{ left: `${ddContext.offsetX()}px`, top: `${ddContext.offsetY()}px`, "max-height": `calc(100vh - ${ddContext.offsetY()}px)` }}
            onMouseEnter={() => ddContext.setIsHoveringContent(true)}
            onMouseLeave={() => ddContext.setIsHoveringContent(false)}
        >
            <Dynamic
                component={local.as ?? 'div'}
                id={ddContext.contentId}
                role={defaultRole()}
                {...other}
                style={{
                    "max-height": `calc(100vh - ${ddContext.offsetY()}px)`,
                    "overflow-y": "auto",
                    ...(props.style ?? {}),
                }}
            >
                {props.children}
            </Dynamic>
        </div>
    )
}


// Make sure we clean up globalClickHandler in dev mode. Otherwise, it will be added multiple times and cause all kinds of weird bugs...
if (import.meta.hot) {
    import.meta.hot.accept((_) => {
        document.removeEventListener('mousedown', globalClickHandler)
        document.removeEventListener('keydown', globalKeyHandler)
    })
}